import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BadgePercent, MessageCircle, ArrowRight, Download, Factory, ShieldCheck, Zap, MapPin, Award, Sun, Battery, Cpu, Plug } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import TrustBadges from "@/components/TrustBadges";
import { setSEO } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMG = "/images/gronsol";
const WHATSAPP = "918605657016";
const wa = (msg) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`;

const TABS = [
  { key: "all", label: "All Gronsol" },
  { key: "ongrid", label: "On-Grid Inverters", test: (p) => p.category === "On Grid Inverter" },
  { key: "hybrid", label: "Hybrid Inverters", test: (p) => p.category === "Hybrid Inverter" },
  { key: "micro", label: "Micro Inverters", test: (p) => p.category === "Micro Inverter" || p.category === "Connector" },
  { key: "battery", label: "Batteries & Storage", test: (p) => p.category === "Battery" },
  { key: "acc", label: "Protection", test: (p) => p.category === "Switchgear & Protection" },
];

const RANGE = [
  { tab: "ongrid", icon: Sun, title: "On-Grid Inverters", sub: "1.5 kW - 136 kW, single & three phase", img: `${IMG}/ongrid-3ph-18-25kw.jpg` },
  { tab: "hybrid", icon: Zap, title: "Hybrid Inverters", sub: "3 kW - 80 kW, LV & HV battery ready", img: `${IMG}/hybrid-1ph-3-8kw.jpg` },
  { tab: "micro", icon: Cpu, title: "Micro Inverters", sub: "1 kW & 2.25 kW, module-level MPPT", img: `${IMG}/micro-2.25kw-4mppt.jpg` },
  { tab: "battery", icon: Battery, title: "LFP Batteries", sub: "4.8 - 16 kWh LV, HV rack systems", img: `${IMG}/battery-lv-5kwh.jpg` },
];

const WHY = [
  { icon: Factory, title: "Made in India", text: "Designed and manufactured at Gronsol's Surat, Gujarat facility, built for Indian grid and weather conditions." },
  { icon: ShieldCheck, title: "Long warranties", text: "10 years on on-grid and micro inverters, 5 years (10 optional) on hybrids, up to 10 years on LFP batteries." },
  { icon: Zap, title: "Smart & efficient", text: "Up to 98.7% efficiency, zero export & VSG support, Wi-Fi monitoring and remote software updates." },
  { icon: Award, title: "Backed by experts", text: "An 8-year R&D team, a responsive technical team across India and robust after-sales support." },
];

const BANNERS = [
  { tab: "ongrid", img: `${IMG}/banner-ongrid.jpg`, title: "On-grid for every rooftop", text: "Single phase homes to 136 kW factory plants." },
  { tab: "hybrid", img: `${IMG}/banner-hybrid.jpg`, title: "Hybrid backup, < 10 ms switching", text: "Keep lights and loads running through power cuts." },
  { tab: "micro", img: `${IMG}/banner-micro.jpg`, title: "Micro inverters beat shading", text: "Every panel tracked on its own, IP67 rated." },
  { tab: "battery", img: `${IMG}/banner-battery.jpg`, title: "LiFePO4 storage that lasts", text: "6000+ cycles, stack up to 32 in parallel." },
];

const SHEETS = [
  ["On-grid 1-phase 1-4.6 kW", "gronsol-ongrid-1ph-1-4.6kw.pdf"], ["On-grid 1-phase 5-6 kW", "gronsol-ongrid-1ph-5-6kw.pdf"],
  ["On-grid 3-phase 5-12 kW", "gronsol-ongrid-3ph-5-12kw.pdf"], ["On-grid 3-phase 15 kW", "gronsol-ongrid-3ph-15kw.pdf"],
  ["On-grid 3-phase 18-25 kW", "gronsol-ongrid-3ph-18-25kw.pdf"], ["On-grid 3-phase 30-35 kW", "gronsol-ongrid-3ph-30-35kw.pdf"],
  ["On-grid 3-phase 40-50 kW", "gronsol-ongrid-3ph-40-50kw.pdf"], ["On-grid 3-phase 60-80 kW", "gronsol-ongrid-3ph-60-80kw.pdf"],
  ["On-grid 3-phase 90-110 kW", "gronsol-ongrid-3ph-90-110kw.pdf"], ["On-grid 3-phase 120-136 kW", "gronsol-ongrid-3ph-120-136kw.pdf"],
  ["Hybrid 1-phase 3-8 kW", "gronsol-hybrid-1ph-3-8kw.pdf"], ["Hybrid 3-phase 5-12 kW", "gronsol-hybrid-3ph-5-12kw.pdf"],
  ["Hybrid 3-phase LV 14-20 kW", "gronsol-hybrid-3ph-14-20kw-lv.pdf"], ["Hybrid 3-phase HV 30 kW", "gronsol-hybrid-3ph-30kw-hv.pdf"],
  ["Hybrid 3-phase HV 40 kW", "gronsol-hybrid-3ph-40kw-hv.pdf"], ["Hybrid 3-phase HV 50 kW", "gronsol-hybrid-3ph-50kw-hv.pdf"],
  ["Hybrid 3-phase HV 60 kW", "gronsol-hybrid-3ph-60kw-hv.pdf"], ["Hybrid 3-phase HV 80 kW", "gronsol-hybrid-3ph-80kw-hv.pdf"],
  ["Micro inverter 600-1200 W", "gronsol-micro-600-1200w.pdf"], ["Micro inverter 1600-2500 W", "gronsol-micro-1600-2500w.pdf"],
  ["LFP battery 4.8 / 5.12 kWh", "gronsol-battery-lv-4.8-5.12kwh.pdf"], ["LFP battery 12 / 16 kWh", "gronsol-battery-lv-12-16kwh.pdf"],
];

const INSTALLS = ["Pune", "Nashik", "Nagpur", "Varanasi", "Raipur", "Ajmer", "Andhra Pradesh"];
const EXPOS = ["Intersolar India 2025", "UP Energy Expo, Lucknow 2025", "Green Energy India Expo, AP 2025", "RE-Network Expo, Ahmedabad 2025"];

export default function GronsolPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    setSEO({
      title: "Gronsol Solar Inverters & LFP Batteries - Flat 5% OFF",
      description: "Buy Gronsol Make in India on-grid, hybrid and micro solar inverters (1.5 kW - 136 kW) and LiFePO4 batteries at 5% off list price. Datasheets, 10-year warranty, delivery across India from Glannu Solar Store.",
      canonical: "https://solar.glannu.com/brands/gronsol",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Brand",
        name: "Gronsol",
        url: "https://solar.glannu.com/brands/gronsol",
        logo: "https://solar.glannu.com/images/gronsol/brand-hero.jpg",
        description: "Gronsol Inverter Technology LLP - Make in India manufacturer of on-grid, hybrid and micro solar inverters and lithium batteries, Surat, Gujarat.",
      },
    });
  }, []);

  useEffect(() => {
    let live = true;
    const load = () => axios.get(`${API}/products`, { params: { brand: "Gronsol", limit: 200, sort: "featured" } })
      .then(({ data }) => { if (live) setProducts((data.products || []).filter((p) => !p.deleted)); })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false); });
    load();
    // The site first paints from a cached snapshot; redraw when newer live data arrives.
    window.addEventListener("glannu:data-updated", load);
    return () => { live = false; window.removeEventListener("glannu:data-updated", load); };
  }, []);

  const shown = useMemo(() => {
    const t = TABS.find((x) => x.key === tab);
    return t && t.test ? products.filter(t.test) : products;
  }, [products, tab]);

  const pick = (key) => {
    setTab(key);
    const el = document.getElementById("gronsol-products");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <nav className="flex items-center gap-2 text-sm text-neutral-500 mb-5">
        <Link to="/" className="hover:text-brand-primary">Home</Link>
        <span>/</span><span className="text-neutral-900 font-medium">Gronsol</span>
      </nav>

      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-100 p-5 sm:p-8 mb-8" data-testid="gronsol-hero">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 text-white text-xs font-bold px-3 py-1 mb-4">
            <BadgePercent className="h-3.5 w-3.5" /> New brand - Flat 5% OFF list price
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-3">
            Gronsol solar inverters &amp; batteries, <span className="text-emerald-700">Made in India</span>
          </h1>
          <p className="text-neutral-600 text-sm sm:text-base leading-relaxed mb-6 max-w-xl">
            On-grid, hybrid and micro inverters from 1.5 kW to 136 kW plus LiFePO4 batteries - manufactured in Surat, Gujarat
            and now available at Glannu with <strong>5% off every Gronsol list price</strong>, GST invoice and delivery across India.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => pick("all")} className="bg-brand-primary hover:bg-brand-primary-hover text-white font-semibold px-6 py-5" data-testid="gronsol-shop">
              Shop Gronsol <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <a href={wa("Hi Glannu, I want a quote for Gronsol inverters / batteries.")} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full border-brand-whatsapp text-brand-whatsapp hover:bg-brand-whatsapp hover:text-white font-semibold px-6 py-5">
                <MessageCircle className="h-4 w-4 mr-2" /> Get a Gronsol quote
              </Button>
            </a>
          </div>
        </div>
        <img src={`${IMG}/brand-hero.jpg`} alt="Gronsol on-grid, hybrid and micro inverters with lithium batteries" className="w-full rounded-xl shadow-lg" />
      </section>

      {/* Quick facts */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
        {[["Est. 2024", "Surat, Gujarat"], ["8 years", "R&D team experience"], ["1.5 - 136 kW", "inverter range"], ["Up to 10 yrs", "manufacturer warranty"]].map(([a, b]) => (
          <div key={a} className="rounded-xl border border-neutral-200 bg-white p-4 text-center">
            <p className="font-heading text-xl sm:text-2xl font-bold text-emerald-700">{a}</p>
            <p className="text-xs sm:text-sm text-neutral-500">{b}</p>
          </div>
        ))}
      </section>

      {/* Range */}
      <section className="mb-10">
        <h2 className="font-heading font-semibold text-xl sm:text-2xl text-neutral-900 mb-4">The Gronsol range</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {RANGE.map(({ tab: t, icon: Icon, title, sub, img }) => (
            <button key={t} onClick={() => pick(t)} className="group text-left rounded-xl border border-neutral-200 bg-white overflow-hidden hover:border-brand-primary hover:shadow-md transition-all">
              <div className="aspect-[4/3] bg-neutral-50 flex items-center justify-center p-3">
                <img src={img} alt={`Gronsol ${title}`} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform" loading="lazy" />
              </div>
              <div className="p-3">
                <p className="font-semibold text-sm text-neutral-900 flex items-center gap-1.5"><Icon className="h-4 w-4 text-brand-primary" /> {title}</p>
                <p className="text-xs text-neutral-500 mt-0.5">{sub}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Products */}
      <section id="gronsol-products" className="mb-12 scroll-mt-24">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-heading font-semibold text-xl sm:text-2xl text-neutral-900">Gronsol products <span className="text-sm font-normal text-neutral-400">({shown.length})</span></h2>
          <span className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-full px-3 py-1">5% OFF applied on every item</span>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`gronsol-tab-${t.key}`}
              className={`flex-shrink-0 text-sm px-4 py-2 rounded-full border transition-colors ${tab === t.key ? "bg-brand-primary text-white border-brand-primary" : "border-neutral-200 text-neutral-700 hover:border-neutral-300"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {[...Array(8)].map((_, i) => <ProductCard key={i} loading />)}
          </div>
        ) : shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
            Gronsol products are being added. Message us on WhatsApp for today's prices.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-testid="gronsol-grid">
            {shown.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* Lifestyle banners */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
        {BANNERS.map((b) => (
          <button key={b.tab} onClick={() => pick(b.tab)} className="relative rounded-2xl overflow-hidden text-left group h-48 sm:h-56">
            <img src={b.img} alt={b.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
            <div className="absolute bottom-0 p-4 sm:p-5">
              <p className="text-white font-heading font-semibold text-lg">{b.title}</p>
              <p className="text-white/80 text-sm">{b.text}</p>
            </div>
          </button>
        ))}
      </section>

      {/* Why Gronsol */}
      <section className="mb-12">
        <h2 className="font-heading font-semibold text-xl sm:text-2xl text-neutral-900 mb-4">Why choose Gronsol?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHY.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-xl border border-neutral-200 bg-white p-5">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-3"><Icon className="h-5 w-5 text-emerald-700" /></div>
              <p className="font-semibold text-neutral-900 mb-1">{title}</p>
              <p className="text-sm text-neutral-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Presence */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center mb-12 rounded-2xl bg-neutral-50 border border-neutral-200 p-5 sm:p-8">
        <img src={`${IMG}/india-presence.jpg`} alt="Gronsol presence across India" className="w-full max-w-md mx-auto rounded-xl" loading="lazy" />
        <div>
          <h2 className="font-heading font-semibold text-xl sm:text-2xl text-neutral-900 mb-3">Installed across India</h2>
          <p className="text-sm text-neutral-600 mb-4">Gronsol inverters are running on rooftops and plants across the country, including:</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {INSTALLS.map((c) => <span key={c} className="inline-flex items-center gap-1 text-sm bg-white border border-neutral-200 rounded-full px-3 py-1"><MapPin className="h-3.5 w-3.5 text-brand-primary" />{c}</span>)}
          </div>
          <p className="text-sm font-semibold text-neutral-900 mb-2">Seen at</p>
          <ul className="text-sm text-neutral-600 space-y-1">
            {EXPOS.map((e) => <li key={e} className="flex items-center gap-2"><Award className="h-4 w-4 text-amber-500" />{e}</li>)}
          </ul>
        </div>
      </section>

      {/* Datasheets */}
      <section className="mb-12" data-testid="gronsol-datasheets">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <h2 className="font-heading font-semibold text-xl sm:text-2xl text-neutral-900">Gronsol datasheets</h2>
          <a href="/datasheets/gronsol-product-brochure.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-primary hover:underline">
            <Download className="h-4 w-4" /> Full product brochure (PDF)
          </a>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {SHEETS.map(([label, file]) => (
            <a key={file} href={`/datasheets/${file}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-700 hover:border-brand-primary hover:text-brand-primary transition-colors">
              <Download className="h-4 w-4 flex-shrink-0" /> {label}
            </a>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="rounded-2xl bg-emerald-800 text-white p-6 sm:p-8 mb-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
        <div>
          <p className="font-heading text-xl sm:text-2xl font-bold mb-1">Planning a Gronsol system?</p>
          <p className="text-white/80 text-sm max-w-xl">Tell us your load or roof size - we'll suggest the right Gronsol inverter, battery and protection, with 5% off list price and freight to your site.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <a href={wa("Hi Glannu, please suggest a Gronsol solar system for me.")} target="_blank" rel="noopener noreferrer">
            <Button className="w-full bg-brand-whatsapp hover:brightness-110 text-white font-semibold px-6 py-5"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp us</Button>
          </a>
          <Link to="/calculator">
            <Button variant="outline" className="w-full bg-transparent border-white text-white hover:bg-white hover:text-emerald-800 font-semibold px-6 py-5"><Plug className="h-4 w-4 mr-2" /> Size my system</Button>
          </Link>
        </div>
      </section>

      <TrustBadges className="mb-6" />
    </main>
  );
}
